
import React, { useState, useEffect, useMemo } from 'react';
import { LogOut, User as UserIcon, Clock, Check, XCircle, Loader2, AlertTriangle, Database, Copy, ExternalLink, Zap, QrCode, FileText, ChevronLeft, ChevronRight, Calendar, Plane, RefreshCw, Lock, Bell, MessageSquare, Send, Activity, BarChart3, Scan, History, Moon, Sun, X, Briefcase, CalendarDays, Home } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react'; 
import { User, Role, AuthState, LogType, AppConfig, AttendanceLog, LeaveRecord, LeaveType, NotificationType, NotificationStatus, Notification } from './types';
import * as storage from './services/storage';
import { AdminDashboard } from './components/AdminDashboard';
import QRScanner from './components/QRScanner';

// --- Helper Functions ---
const timeStringToMinutes = (timeStr: string): number => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return (hours * 60) + minutes;
};

const minutesToDuration = (minutes: number): string => {
    if (!minutes || minutes === 0) return "-";
    const absMin = Math.abs(minutes);
    const h = Math.floor(absMin / 60);
    const m = absMin % 60;
    const timeStr = h > 0 ? `${h}s ${m}dk` : `${m}dk`;
    return minutes < 0 ? `-${timeStr}` : timeStr;
};

// FIX: Force Istanbul Timezone for Calculations
const getMinutesFromTimestamp = (timestamp: number): number => {
    try {
        const date = new Date(timestamp);
        const timeStr = date.toLocaleTimeString('en-US', { 
            timeZone: 'Europe/Istanbul', 
            hour12: false, 
            hour: '2-digit', 
            minute: '2-digit' 
        });
        if (timeStr === '24:00') return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return (h * 60) + m;
    } catch (e) {
        const date = new Date(timestamp);
        return (date.getHours() * 60) + date.getMinutes();
    }
};

const formatTimeTR = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Istanbul'
    });
};

const calculateLeaveEntitlement = (startDateStr?: string): number => {
    if (!startDateStr) return 14;
    const start = new Date(startDateStr);
    const now = new Date();
    let years = now.getFullYear() - start.getFullYear();
    const m = now.getMonth() - start.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < start.getDate())) years--;

    if (years < 1) return 0;
    if (years < 6) return 14;
    if (years < 15) return 20;
    return 26;
};

// --- QR KIOSK COMPONENT (Dark Theme) ---
const QRKiosk: React.FC<{ config: AppConfig | null, onLogout: () => void }> = ({ config: initialConfig, onLogout }) => {
    const [currentCode, setCurrentCode] = useState<string | null>(initialConfig?.qrSecret || null);
    const refreshSeconds = initialConfig?.qrRefreshSeconds || 15;
    const [timeLeft, setTimeLeft] = useState(refreshSeconds);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        if (!currentCode && initialConfig?.qrSecret) {
            setCurrentCode(initialConfig.qrSecret);
        } else if (!currentCode) {
            storage.getConfig().then(c => {
                 if(c.qrSecret) setCurrentCode(c.qrSecret);
                 else setCurrentCode("TECHNOSIS_INIT"); 
            });
        }
    }, [initialConfig]);

    useEffect(() => {
        let mounted = true;
        
        const updateQR = async () => {
            if (!mounted) return;
            setIsUpdating(true);
            const newSecret = `TECHNOSIS_DYNAMIC_${Date.now()}_${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
            
            try {
                // Update Config in DB so User can verify against it
                const current = await storage.getConfig();
                await storage.updateConfig({ ...current, qrSecret: newSecret });
                
                if (mounted) { 
                    setCurrentCode(newSecret); 
                    const freshSeconds = current.qrRefreshSeconds || 15; 
                    setTimeLeft(freshSeconds); 
                }
            } catch (e) { 
                console.error("Auto QR Update Failed:", e); 
            } finally { 
                if (mounted) setIsUpdating(false); 
            }
        };

        if (!currentCode) updateQR();

        const timerInterval = setInterval(() => { setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0)); }, 1000);
        
        let timeoutId: any;
        const scheduleUpdate = () => {
             timeoutId = setTimeout(() => { 
                 updateQR().then(() => { 
                     if (mounted) scheduleUpdate(); 
                 }); 
             }, (refreshSeconds * 1000));
        };
        
        scheduleUpdate();
        return () => { mounted = false; clearInterval(timerInterval); clearTimeout(timeoutId); };
    }, [refreshSeconds]);

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 text-white relative overflow-hidden font-sans select-none">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[100px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 rounded-full blur-[100px] animate-pulse delay-1000"></div>
            </div>
            <div className="z-10 w-full max-w-4xl flex flex-col items-center gap-12">
                <div className="text-center space-y-2">
                    <h1 className="text-4xl md:text-6xl font-black tracking-tight text-white drop-shadow-lg">TECHNOSİS</h1>
                    <p className="text-slate-500 text-lg font-medium tracking-widest uppercase">Personel Takip Terminali</p>
                </div>
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-[2.5rem] blur opacity-50 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                    <div className="relative bg-slate-900 p-10 rounded-[2rem] shadow-2xl flex flex-col items-center gap-6 border border-slate-800">
                        {(!currentCode || isUpdating && !currentCode) ? (
                            <div className="w-[300px] h-[300px] flex items-center justify-center">
                                <Loader2 className="w-16 h-16 text-slate-500 animate-spin" />
                            </div> 
                        ) : (
                            <div className="bg-white p-2 rounded-xl">
                                <QRCodeSVG value={currentCode} size={280} level={"M"} includeMargin={true} />
                            </div>
                        )}
                        <div className="w-full bg-slate-800 rounded-full h-2 overflow-hidden"><div className="bg-blue-600 h-full transition-all duration-1000 ease-linear" style={{ width: `${(timeLeft / refreshSeconds) * 100}%` }}></div></div>
                        <div className="flex items-center gap-2 text-slate-400 font-mono text-sm font-bold"><RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />{isUpdating ? 'KOD YENİLENİYOR...' : `YENİ KOD: ${timeLeft} SANİYE`}</div>
                    </div>
                </div>
            </div>
            <button onClick={onLogout} className="absolute bottom-6 right-6 p-3 text-slate-600 hover:text-white transition-colors flex items-center gap-2 bg-slate-900 rounded-xl border border-slate-800 px-4 py-2 hover:bg-red-900/20"><LogOut className="w-5 h-5" /><span className="font-bold">Çıkış Yap</span></button>
        </div>
    );
};

// Auth Component (Dark Theme)
const Login: React.FC<{ onLogin: (u: User) => void }> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
        const user = await storage.loginUser(username, password);
        if (user) { onLogin(user); } else { setError('Hatalı kullanıcı adı veya şifre'); }
    } catch (e) { setError('Giriş sırasında bir hata oluştu.'); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 relative overflow-hidden">
      <div className="bg-slate-900 p-8 rounded-3xl shadow-2xl w-full max-w-md relative z-10 border border-slate-800">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6"><Clock className="w-8 h-8 text-white" /></div>
          <h2 className="text-2xl font-bold text-white mb-2">Personel Girişi</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Kullanıcı Adı</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-white" placeholder="Kullanıcı adınız" required />
          </div>
          <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Şifre</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 bg-slate-950 border border-slate-800 rounded-xl text-white" placeholder="••••••••" required />
          </div>
          {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3.5 rounded-xl font-bold">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Giriş Yap'}
          </button>
        </form>
      </div>
    </div>
  );
};

// --- USER DASHBOARD ---
const UserDashboard: React.FC<{ user: User; onLogout: () => void }> = ({ user, onLogout }) => {
    const [activeTab, setActiveTab] = useState<'home' | 'reports' | 'leaves' | 'notifications'>('home');
    const [showQRScanner, setShowQRScanner] = useState(false);
    const [scanStatus, setScanStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [selectedReportMonth, setSelectedReportMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    
    // Data States
    const [logs, setLogs] = useState<AttendanceLog[]>([]);
    const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isDataLoading, setIsDataLoading] = useState(true);
    
    // Clock
    const [currentTime, setCurrentTime] = useState(new Date());
    const [timeOffset, setTimeOffset] = useState(0);

    // Forms
    const [requestForm, setRequestForm] = useState({ type: NotificationType.FORGOT_QR, desc: '', date: new Date().toISOString().split('T')[0], time: '08:00' });

    const loadUserData = async () => {
        setIsDataLoading(true);
        try {
            const [l, conf, lev, notifs] = await Promise.all([
                storage.getLogsByUser(user.id),
                storage.getConfig(),
                storage.getLeavesByUser(user.id),
                storage.getNotificationsByUser(user.id)
            ]);
            setLogs(l); 
            setConfig(conf); 
            setLeaves(lev); 
            setNotifications(notifs);
        } catch (error) {
            console.error("Data load error", error);
        } finally {
            setIsDataLoading(false);
        }
    };

    useEffect(() => {
        loadUserData();
        
        // Sync Time
        storage.fetchRealTime().then(serverTime => {
            const localTime = Date.now();
            setTimeOffset(serverTime - localTime);
        });
    }, [user.id, activeTab]); 

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date(Date.now() + timeOffset));
        }, 1000);
        return () => clearInterval(timer);
    }, [timeOffset]);

    // --- FIX: QR SCAN LOGIC ---
    const handleScan = async (data: string) => {
        // Fetch the LATEST config from server at the moment of scan
        // This solves the issue of the user app having an old 'qrSecret' in state
        try {
            const freshConfig = await storage.getConfig();
            
            if (data !== freshConfig.qrSecret) {
                setScanStatus('error');
                setStatusMessage('Geçersiz veya Süresi Dolmuş QR Kod!');
                return;
            }

            setScanStatus('success');
            // Determine Entry/Exit based on last log
            const lastLog = logs.length > 0 ? logs[0] : null;
            const type = (lastLog && lastLog.type === LogType.ENTRY) ? LogType.EXIT : LogType.ENTRY;
            
            setStatusMessage(type === LogType.ENTRY ? `Hoşgeldiniz, ${user.fullName}` : `Güle Güle, ${user.fullName}`);

            const ua = navigator.userAgent;
            await storage.addLog(user.id, user.fullName, type, undefined, 'Mobil Cihaz', ua);
            
            // Refresh logs to update UI
            const updatedLogs = await storage.getLogsByUser(user.id);
            setLogs(updatedLogs);

            setTimeout(() => {
                setShowQRScanner(false);
                setScanStatus('idle');
                setStatusMessage('');
            }, 2500);

        } catch (e) {
            console.error(e);
            setScanStatus('error');
            setStatusMessage('Bağlantı hatası. Lütfen tekrar deneyin.');
        }
    };

    const handleSendRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        await storage.addNotification({
            userId: user.id,
            userFullName: user.fullName,
            type: requestForm.type,
            description: requestForm.desc,
            targetDate: requestForm.date,
            targetTime: requestForm.time
        });
        setNotifications(await storage.getNotificationsByUser(user.id));
        setRequestForm({ ...requestForm, desc: '' });
        alert("Talep gönderildi.");
    };

    // --- CALCULATIONS ---
    const lastLog = logs[0];
    const isInside = lastLog?.type === LogType.ENTRY;
    
    // Monthly Daily Stats (Detailed)
    const calculatedReportData = useMemo(() => {
        if (!config) return [];
        const [year, month] = selectedReportMonth.split('-').map(Number);
        const daysInMonth = new Date(year, month, 0).getDate();
        const reportData = [];

        // Current Date Check for filtering
        const now = new Date();
        const todayStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });

        // Show all days for the selected month
        for (let i = 1; i <= daysInMonth; i++) {
            const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(i).padStart(2,'0')}`;
            
            // Filter out future dates so today appears at the top
            if (dateStr > todayStr) continue;

            const date = new Date(year, month - 1, i);
            const dayOfWeek = date.getDay();

            // Filter Logs by DATE STRING
            const dailyLogs = logs.filter(l => {
                const logDateStr = new Date(l.timestamp).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
                return logDateStr === dateStr;
            }).sort((a,b) => a.timestamp - b.timestamp);

            const firstEntry = dailyLogs.find(l => l.type === LogType.ENTRY);
            const lastExit = dailyLogs.reverse().find(l => l.type === LogType.EXIT);

            let breakMin = 0;
            // Calculate breaks
            const sortedLogs = [...dailyLogs].sort((a,b) => a.timestamp - b.timestamp);
            for(let j = 0; j < sortedLogs.length - 1; j++) {
                if (sortedLogs[j].type === LogType.EXIT && sortedLogs[j+1].type === LogType.ENTRY) {
                    const exitTime = getMinutesFromTimestamp(sortedLogs[j].timestamp);
                    const nextEntryTime = getMinutesFromTimestamp(sortedLogs[j+1].timestamp);
                    if (nextEntryTime > exitTime) {
                         breakMin += (nextEntryTime - exitTime);
                    }
                }
            }

            const isHoliday = config?.publicHolidays?.includes(dateStr);
            const isSunday = dayOfWeek === 0;
            const isSaturday = dayOfWeek === 6;
            const isSatWorkDay = user.shiftConfig?.isSaturdayWorkDay ?? config?.isSaturdayWorkDay ?? false;
            const isFullOvertimeDay = isHoliday || isSunday || (isSaturday && !isSatWorkDay);

            let missingMin = 0;
            let overtimeMin = 0;
            let holidayOvertimeMin = 0;
            let status = '';
            
            const isOnLeave = leaves.some(l => l.startDate <= dateStr && l.endDate >= dateStr);

            if (isOnLeave) {
                status = 'İZİNLİ';
            } else if (firstEntry && lastExit) {
                // Completed Shift
                const entryMin = getMinutesFromTimestamp(firstEntry.timestamp);
                const exitMin = getMinutesFromTimestamp(lastExit.timestamp);
                const duration = exitMin - entryMin;

                const sStart = user.shiftConfig?.start || config?.shiftStart || "08:00";
                const sEnd = user.shiftConfig?.end || config?.shiftEnd || "18:00";
                const satStart = user.shiftConfig?.saturdayStart || config?.saturdayStart || "09:00";
                const satEnd = user.shiftConfig?.saturdayEnd || config?.saturdayEnd || "14:00";

                const targetStartMin = (isSaturday && isSatWorkDay) ? timeStringToMinutes(satStart) : timeStringToMinutes(sStart);
                const targetEndMin = (isSaturday && isSatWorkDay) ? timeStringToMinutes(satEnd) : timeStringToMinutes(sEnd);

                if (isFullOvertimeDay) {
                    if (isHoliday) holidayOvertimeMin = duration - breakMin;
                    else overtimeMin = duration - breakMin;
                    status = 'F.MESAİ';
                } else {
                    let late = 0;
                    let early = 0;
                    if (entryMin > targetStartMin) late = entryMin - targetStartMin;
                    if (exitMin < targetEndMin) early = targetEndMin - exitMin;
                    
                    missingMin = late + early + breakMin;
                    if (exitMin > targetEndMin) overtimeMin = exitMin - targetEndMin;
                    status = 'GELDİ';
                }
            } else if (firstEntry && !lastExit) {
                // Still inside or forgot checkout
                status = 'İÇERİDE';
            } else if (!isFullOvertimeDay && date < new Date(new Date().setHours(0,0,0,0))) {
                status = 'GELMEDİ';
            } else if (isFullOvertimeDay) {
                status = isHoliday ? 'TATİL' : 'H.SONU';
            } else {
                status = '-'; // Future dates
            }

            reportData.push({
                date,
                dateStr,
                dayName: date.toLocaleDateString('tr-TR', {weekday: 'short'}),
                firstEntry,
                lastExit,
                missingMin,
                overtimeMin,
                holidayOvertimeMin,
                breakMin,
                status
            });
        }
        return reportData.reverse(); // Newest first
    }, [selectedReportMonth, logs, config, user, leaves]);

    // Current Month Summary Stats
    const currentMonthStats = useMemo(() => {
        if (!config) return { totalMissingMin: 0, totalOvertimeMin: 0, totalHolidayOvertimeMin: 0, workDays: 0, absentDays: 0 };
        
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const daysInMonth = now.getDate(); // Calculate up to today

        let totalMissingMin = 0;
        let totalOvertimeMin = 0;
        let totalHolidayOvertimeMin = 0;
        let workDays = 0;
        let absentDays = 0;

        for (let i = 1; i <= daysInMonth; i++) {
             const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(i).padStart(2,'0')}`;
             const date = new Date(year, month, i);
             const dayOfWeek = date.getDay();
             
             // Timezone Aware Filter
             const dailyLogs = logs.filter(l => {
                const logDateStr = new Date(l.timestamp).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
                return logDateStr === dateStr;
             });

             const firstEntry = dailyLogs.find(l => l.type === LogType.ENTRY);
             const lastExit = dailyLogs.reverse().find(l => l.type === LogType.EXIT);

             // Break calculation
             let breakMin = 0;
             const sortedLogs = [...dailyLogs].sort((a,b) => a.timestamp - b.timestamp);
             for(let j = 0; j < sortedLogs.length - 1; j++) {
                if (sortedLogs[j].type === LogType.EXIT && sortedLogs[j+1].type === LogType.ENTRY) {
                    const exitTime = getMinutesFromTimestamp(sortedLogs[j].timestamp);
                    const nextEntryTime = getMinutesFromTimestamp(sortedLogs[j+1].timestamp);
                    if (nextEntryTime > exitTime) {
                         breakMin += (nextEntryTime - exitTime);
                    }
                }
             }
             
             const isHoliday = config?.publicHolidays?.includes(dateStr);
             const isSunday = dayOfWeek === 0;
             const isSaturday = dayOfWeek === 6;
             const isSatWorkDay = user.shiftConfig?.isSaturdayWorkDay ?? config?.isSaturdayWorkDay ?? false;
             const isFullOvertimeDay = isHoliday || isSunday || (isSaturday && !isSatWorkDay);

             const sStart = user.shiftConfig?.start || config?.shiftStart || "08:00";
             const sEnd = user.shiftConfig?.end || config?.shiftEnd || "18:00";
             const satStart = user.shiftConfig?.saturdayStart || config?.saturdayStart || "09:00";
             const satEnd = user.shiftConfig?.saturdayEnd || config?.saturdayEnd || "14:00";
             const targetStartMin = (isSaturday && isSatWorkDay) ? timeStringToMinutes(satStart) : timeStringToMinutes(sStart);
             const targetEndMin = (isSaturday && isSatWorkDay) ? timeStringToMinutes(satEnd) : timeStringToMinutes(sEnd);

             const isOnLeave = leaves.some(l => l.startDate <= dateStr && l.endDate >= dateStr);

             if (isOnLeave) {
                 // Nothing
             } else if (firstEntry && lastExit) {
                 workDays++;
                 const entryMin = getMinutesFromTimestamp(firstEntry.timestamp);
                 const exitMin = getMinutesFromTimestamp(lastExit.timestamp);
                 const duration = exitMin - entryMin;

                 if (isFullOvertimeDay) {
                     if (isHoliday) totalHolidayOvertimeMin += (duration - breakMin);
                     else totalOvertimeMin += (duration - breakMin);
                 } else {
                     if (entryMin > targetStartMin) totalMissingMin += (entryMin - targetStartMin);
                     if (exitMin < targetEndMin) totalMissingMin += (targetEndMin - exitMin);
                     totalMissingMin += breakMin;

                     if (exitMin > targetEndMin) totalOvertimeMin += (exitMin - targetEndMin);
                 }
             } else if (!isFullOvertimeDay && date < new Date(now.setHours(0,0,0,0))) {
                 absentDays++;
             }
        }
        return { totalMissingMin, totalOvertimeMin, totalHolidayOvertimeMin, workDays, absentDays };
    }, [logs, config, user, leaves]);


    const leaveEntitlement = useMemo(() => calculateLeaveEntitlement(user.employmentStartDate), [user]);
    const usedAnnualLeave = useMemo(() => leaves.filter(l => l.type === LeaveType.ANNUAL).reduce((acc, curr) => acc + curr.daysDeducted, 0), [leaves]);
    const remainingLeave = Math.max(0, leaveEntitlement - usedAnnualLeave);
    const sickLeaveDays = useMemo(() => leaves.filter(l => l.type === LeaveType.SICK).reduce((acc, curr) => acc + curr.daysDeducted, 0), [leaves]);

    // Format Clock in Turkey Time
    const timeString = currentTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' });
    const dateString = currentTime.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Istanbul' });

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 pb-24 font-sans selection:bg-blue-500/30">
            {/* Header */}
            <header className="fixed top-0 left-0 w-full bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 z-40">
                <div className="max-w-md mx-auto px-4 py-3 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold shadow-lg shadow-blue-900/20">
                            {user.fullName.charAt(0)}
                        </div>
                        <div>
                            <h1 className="text-sm font-bold text-white leading-tight">{user.fullName}</h1>
                            <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{user.department}</p>
                        </div>
                    </div>
                    
                    {/* Logout Button */}
                    <button onClick={onLogout} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-red-400 transition-colors border border-slate-700">
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>
            </header>

            <main className="max-w-md mx-auto px-4 pt-24 space-y-6">
                
                {/* --- HOME TAB --- */}
                {activeTab === 'home' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
                        {/* Hero Status Card */}
                        <div className={`relative overflow-hidden rounded-3xl p-6 shadow-2xl transition-all duration-500 ${isInside ? 'bg-gradient-to-br from-green-600 to-emerald-800 shadow-green-900/30' : 'bg-gradient-to-br from-slate-800 to-slate-900 shadow-black/50 border border-slate-700'}`}>
                             {isInside && <div className="absolute top-[-50%] right-[-20%] w-64 h-64 bg-white/10 rounded-full blur-[60px] animate-pulse"></div>}
                             
                             <div className="relative z-10 flex flex-col items-center text-center space-y-2">
                                 <div className={`p-4 rounded-full mb-2 ${isInside ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-400'}`}>
                                     {isInside ? <Check className="w-8 h-8" /> : <LogOut className="w-8 h-8" />}
                                 </div>
                                 <h2 className="text-3xl font-black text-white tracking-tight">
                                     {isInside ? 'İçeridesiniz' : 'Dışarıdasınız'}
                                 </h2>
                                 <p className={`text-sm font-medium ${isInside ? 'text-green-100' : 'text-slate-400'}`}>
                                     {isInside ? 'Mesainiz devam ediyor.' : 'Şu an mesai dışındasınız.'}
                                 </p>
                                 <div className="mt-2 text-xl font-bold font-mono text-white tracking-wide">
                                     {timeString}
                                 </div>
                                 <div className="text-xs text-slate-400 font-medium">
                                     {dateString}
                                 </div>
                                 {lastLog && (
                                     <div className={`mt-4 px-4 py-1.5 rounded-full text-xs font-mono font-bold ${isInside ? 'bg-black/20 text-green-100' : 'bg-black/30 text-slate-400'}`}>
                                         Son Hareket: {formatTimeTR(lastLog.timestamp)}
                                     </div>
                                 )}
                             </div>
                        </div>

                        {/* Main Action - QR */}
                        <button onClick={() => setShowQRScanner(true)} className="w-full py-4 bg-blue-600 hover:bg-blue-500 active:scale-95 transition-all rounded-2xl flex items-center justify-center gap-3 text-white font-bold text-lg shadow-xl shadow-blue-900/30 group">
                            <div className="p-2 bg-blue-700 rounded-xl group-hover:bg-blue-600 transition-colors"><Scan className="w-6 h-6" /></div>
                            QR Kod Okut
                        </button>

                        {/* Quick Stats Grid (2 Column) */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-bold text-slate-500 uppercase ml-1">Bu Ayın Özeti</h3>
                            <div className="grid grid-cols-2 gap-3">
                                 {/* Leaves (Moved from bottom to Top) */}
                                 <div className="col-span-2 p-4 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col justify-between h-24 flex-row items-center">
                                     <div className="flex items-center gap-3">
                                         <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500"><Plane className="w-5 h-5" /></div>
                                         <div>
                                            <div className="text-xl font-bold text-blue-500">{remainingLeave} Gün</div>
                                            <div className="text-[10px] text-slate-500 font-bold uppercase">Kalan İzin</div>
                                         </div>
                                     </div>
                                     <div className="text-right">
                                         <div className="text-sm font-bold text-white">{currentMonthStats.workDays} Gün</div>
                                         <div className="text-[10px] text-slate-500 font-bold uppercase">Çalışılan</div>
                                     </div>
                                 </div>
                                 
                                 {/* Missing Duration */}
                                 <div className="p-4 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col justify-between h-32 relative overflow-hidden">
                                     <div className="absolute top-0 right-0 w-16 h-16 bg-red-500/5 rounded-bl-full"></div>
                                     <Clock className="w-5 h-5 text-red-500 mb-2" />
                                     <div>
                                         <div className="text-xl font-bold text-red-500">{minutesToDuration(currentMonthStats.totalMissingMin)}</div>
                                         <div className="text-[10px] text-slate-500 font-bold uppercase">Eksik Süre</div>
                                     </div>
                                 </div>

                                 {/* Overtime */}
                                 <div className="p-4 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col justify-between h-32 relative overflow-hidden">
                                     <div className="absolute top-0 right-0 w-16 h-16 bg-green-500/5 rounded-bl-full"></div>
                                     <Zap className="w-5 h-5 text-green-500 mb-2" />
                                     <div>
                                         <div className="text-xl font-bold text-green-500">{minutesToDuration(currentMonthStats.totalOvertimeMin)}</div>
                                         <div className="text-[10px] text-slate-500 font-bold uppercase">Fazla Mesai</div>
                                     </div>
                                 </div>

                                 {/* Holiday Overtime (Made wide to balance grid) */}
                                 <div className="col-span-2 p-4 bg-slate-900 rounded-2xl border border-slate-800 flex flex-col justify-between h-24 flex-row items-center relative overflow-hidden">
                                     <div className="absolute top-0 right-0 w-16 h-16 bg-purple-500/5 rounded-bl-full"></div>
                                     <div className="flex items-center gap-3 relative z-10">
                                         <div className="p-2 bg-purple-500/10 rounded-lg text-purple-500"><Sun className="w-5 h-5" /></div>
                                         <div>
                                            <div className="text-xl font-bold text-purple-500">{minutesToDuration(currentMonthStats.totalHolidayOvertimeMin)}</div>
                                            <div className="text-[10px] text-slate-500 font-bold uppercase">Bayram Mesai</div>
                                         </div>
                                     </div>
                                 </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- REPORTS TAB --- */}
                {activeTab === 'reports' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-bold text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-500"/> Aylık Rapor</h2>
                        </div>
                        
                        {/* Month Selector */}
                        <div className="bg-slate-900 p-3 rounded-xl border border-slate-800">
                             <input 
                                type="month" 
                                value={selectedReportMonth} 
                                onChange={(e) => setSelectedReportMonth(e.target.value)}
                                className="w-full bg-slate-950 text-white font-bold p-2 rounded-lg border border-slate-800 focus:ring-2 focus:ring-blue-500 outline-none"
                             />
                        </div>

                        {/* Daily Stats List */}
                        <div className="space-y-3 pb-20">
                             {calculatedReportData.length === 0 && <div className="text-center py-10 text-slate-500">Kayıt bulunamadı.</div>}
                             {calculatedReportData.map((day, idx) => (
                                 <div key={idx} className="bg-slate-900 p-4 rounded-xl border border-slate-800 hover:border-slate-600 transition-colors">
                                     {/* Header: Date & Status */}
                                     <div className="flex justify-between items-center mb-3 border-b border-slate-800 pb-2">
                                         <div className="flex items-center gap-2">
                                             <div className="w-10 h-10 bg-slate-800 rounded-lg flex flex-col items-center justify-center border border-slate-700">
                                                 <span className="text-[10px] text-slate-400 font-bold uppercase">{day.dayName}</span>
                                                 <span className="text-sm font-black text-white">{day.date.getDate()}</span>
                                             </div>
                                             <div>
                                                 <div className="text-xs text-slate-500 font-medium">Giriş / Çıkış</div>
                                                 <div className="text-sm font-mono font-bold text-white">
                                                     {day.firstEntry ? formatTimeTR(day.firstEntry.timestamp) : '--:--'} 
                                                     <span className="text-slate-600 mx-1">/</span> 
                                                     {day.lastExit ? formatTimeTR(day.lastExit.timestamp) : '--:--'}
                                                 </div>
                                             </div>
                                         </div>
                                         <div className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${day.status === 'GELDİ' ? 'bg-green-500/10 text-green-400 border-green-500/20' : day.status === 'GELMEDİ' ? 'bg-red-500/10 text-red-400 border-red-500/20' : day.status === 'İZİNLİ' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>
                                             {day.status}
                                         </div>
                                     </div>

                                     {/* Stats Row */}
                                     <div className="flex gap-2">
                                         {day.breakMin > 0 && (
                                              <div className="flex-1 bg-orange-950/30 rounded px-2 py-1.5 flex items-center justify-between border border-orange-900/30">
                                                 <span className="text-[10px] text-orange-400 font-bold">Ara</span>
                                                 <span className="text-xs font-mono font-bold text-orange-400">{minutesToDuration(day.breakMin)}</span>
                                              </div>
                                         )}

                                         {day.missingMin > 0 && (
                                             <div className="flex-1 bg-red-950/30 rounded px-2 py-1.5 flex items-center justify-between border border-red-900/30">
                                                 <span className="text-[10px] text-red-400 font-bold">Eksik</span>
                                                 <span className="text-xs font-mono font-bold text-red-400">{minutesToDuration(day.missingMin)}</span>
                                             </div>
                                         )}
                                         {(day.overtimeMin > 0 || day.holidayOvertimeMin > 0) ? (
                                             <div className="flex-1 bg-green-950/30 rounded px-2 py-1.5 flex items-center justify-between border border-green-900/30">
                                                 <span className="text-[10px] text-green-400 font-bold">F.Mesai</span>
                                                 <span className="text-xs font-mono font-bold text-green-400">{minutesToDuration(day.overtimeMin + day.holidayOvertimeMin)}</span>
                                             </div>
                                         ) : (day.missingMin === 0 && day.status === 'GELDİ') && (
                                             <div className="flex-1 bg-blue-950/30 rounded px-2 py-1.5 text-center border border-blue-900/30">
                                                 <span className="text-[10px] text-blue-400 font-bold">Tam Zamanlı</span>
                                             </div>
                                         )}
                                     </div>
                                 </div>
                             ))}
                        </div>
                    </div>
                )}
                
                {/* --- LEAVES TAB --- */}
                {activeTab === 'leaves' && (
                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-4">
                         <div className="bg-gradient-to-br from-blue-900 to-slate-900 p-6 rounded-2xl border border-slate-800 shadow-xl">
                            <h3 className="text-sm font-bold text-slate-400 uppercase mb-1">Kalan Yıllık İzin</h3>
                            <div className="text-4xl font-black text-white">{remainingLeave} <span className="text-xl text-slate-400">Gün</span></div>
                            <div className="mt-4 pt-4 border-t border-slate-700/50 flex justify-between text-xs">
                                <div><span className="text-slate-400 block">Hakedilen</span><span className="text-white font-bold">{leaveEntitlement} Gün</span></div>
                                <div><span className="text-slate-400 block">Kullanılan</span><span className="text-white font-bold">{usedAnnualLeave} Gün</span></div>
                            </div>
                         </div>
                         
                         <h3 className="text-sm font-bold text-slate-500 uppercase mt-6 mb-2">İzin Geçmişi</h3>
                         <div className="space-y-3 pb-20">
                             {leaves.length === 0 && <div className="text-center py-8 text-slate-500">İzin kaydı bulunamadı.</div>}
                             {leaves.map(l => (
                                 <div key={l.id} className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex justify-between items-center">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${l.type === LeaveType.ANNUAL ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}`}>{l.type}</span>
                                            <span className="text-xs text-slate-500">{new Date(l.startDate).toLocaleDateString('tr-TR')}</span>
                                        </div>
                                        <div className="text-xs text-slate-400 italic">"{l.description}"</div>
                                    </div>
                                    <div className="text-lg font-bold text-white">{l.daysDeducted}g</div>
                                 </div>
                             ))}
                         </div>
                    </div>
                )}
                
                {/* --- NOTIFICATIONS TAB --- */}
                {activeTab === 'notifications' && (
                     <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
                         <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800">
                             <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Send className="w-4 h-4 text-blue-500"/> Yeni Talep Oluştur</h3>
                             <form onSubmit={handleSendRequest} className="space-y-3">
                                 <div>
                                     <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Talep Türü</label>
                                     <select value={requestForm.type} onChange={e => setRequestForm({...requestForm, type: e.target.value as NotificationType})} className="w-full bg-slate-950 text-white p-2.5 rounded-lg border border-slate-700 text-sm">
                                         {Object.values(NotificationType).map(t => <option key={t} value={t}>{t}</option>)}
                                     </select>
                                 </div>
                                 <div className="grid grid-cols-2 gap-3">
                                     <div>
                                         <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Tarih</label>
                                         <input type="date" value={requestForm.date} onChange={e => setRequestForm({...requestForm, date: e.target.value})} className="w-full bg-slate-950 text-white p-2.5 rounded-lg border border-slate-700 text-sm" />
                                     </div>
                                     <div>
                                         <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Saat (Opsiyonel)</label>
                                         <input type="time" value={requestForm.time} onChange={e => setRequestForm({...requestForm, time: e.target.value})} className="w-full bg-slate-950 text-white p-2.5 rounded-lg border border-slate-700 text-sm" />
                                     </div>
                                 </div>
                                 <div>
                                     <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Açıklama</label>
                                     <textarea value={requestForm.desc} onChange={e => setRequestForm({...requestForm, desc: e.target.value})} placeholder="Örn: Sabah trafiğe takıldım..." className="w-full bg-slate-950 text-white p-2.5 rounded-lg border border-slate-700 text-sm h-20 resize-none"></textarea>
                                 </div>
                                 <button type="submit" className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-sm transition-colors">Talep Gönder</button>
                             </form>
                         </div>
                         
                         <div className="space-y-3 pb-20">
                             <h3 className="text-xs font-bold text-slate-500 uppercase ml-1">Geçmiş Talepler</h3>
                             {notifications.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">Talep bulunmuyor.</div>}
                             {notifications.map(n => (
                                 <div key={n.id} className="bg-slate-900 p-4 rounded-xl border border-slate-800">
                                     <div className="flex justify-between items-start mb-2">
                                         <span className="text-xs font-bold text-white bg-slate-800 px-2 py-1 rounded">{n.type}</span>
                                         <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${n.status === NotificationStatus.APPROVED ? 'bg-green-500/20 text-green-400' : n.status === NotificationStatus.REJECTED ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{n.status}</span>
                                     </div>
                                     <p className="text-sm text-slate-300 mb-2">"{n.description}"</p>
                                     <div className="text-[10px] text-slate-500 flex justify-between">
                                         <span>Hedef: {new Date(n.targetDate).toLocaleDateString('tr-TR')} {n.targetTime}</span>
                                         <span>{new Date(n.createdAt).toLocaleDateString('tr-TR')}</span>
                                     </div>
                                 </div>
                             ))}
                         </div>
                     </div>
                )}
            </main>

            {/* --- BOTTOM NAVIGATION --- */}
            <div className="fixed bottom-0 left-0 w-full bg-slate-900/90 backdrop-blur-lg border-t border-slate-800 py-2 pb-6 px-6 flex justify-between items-center z-50">
                <button onClick={() => setActiveTab('home')} className={`flex flex-col items-center gap-1 ${activeTab === 'home' ? 'text-blue-500' : 'text-slate-500'}`}>
                    <Home className="w-6 h-6" />
                    <span className="text-[10px] font-bold">Ana Sayfa</span>
                </button>
                <button onClick={() => setActiveTab('reports')} className={`flex flex-col items-center gap-1 ${activeTab === 'reports' ? 'text-blue-500' : 'text-slate-500'}`}>
                    <BarChart3 className="w-6 h-6" />
                    <span className="text-[10px] font-bold">Rapor</span>
                </button>
                <div className="relative -top-6">
                    <button onClick={() => setShowQRScanner(true)} className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg shadow-blue-600/40 border-4 border-slate-950">
                        <Scan className="w-7 h-7" />
                    </button>
                </div>
                <button onClick={() => setActiveTab('leaves')} className={`flex flex-col items-center gap-1 ${activeTab === 'leaves' ? 'text-blue-500' : 'text-slate-500'}`}>
                    <Plane className="w-6 h-6" />
                    <span className="text-[10px] font-bold">İzinler</span>
                </button>
                <button onClick={() => setActiveTab('notifications')} className={`flex flex-col items-center gap-1 ${activeTab === 'notifications' ? 'text-blue-500' : 'text-slate-500'}`}>
                    <Bell className="w-6 h-6" />
                    <span className="text-[10px] font-bold">Talepler</span>
                </button>
            </div>

            {/* QR Scanner Modal */}
            {showQRScanner && (
                 <div className="fixed inset-0 z-50 bg-black flex flex-col">
                     <div className="relative flex-1">
                        <QRScanner onScan={handleScan} isProcessing={scanStatus === 'success'} />
                        {/* Close Button */}
                        <button onClick={() => setShowQRScanner(false)} className="absolute top-6 right-6 p-3 bg-slate-800/50 backdrop-blur-md text-white rounded-full z-20">
                            <X className="w-6 h-6" />
                        </button>
                        
                        {/* Status Overlay */}
                        {statusMessage && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900/90 backdrop-blur-xl p-6 rounded-2xl border border-slate-700 text-center shadow-2xl z-30 w-[80%]">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${scanStatus === 'success' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                                    {scanStatus === 'success' ? <Check className="w-8 h-8" /> : <AlertTriangle className="w-8 h-8" />}
                                </div>
                                <h3 className="text-white font-bold text-lg mb-1">{scanStatus === 'success' ? 'Başarılı!' : 'Hata!'}</h3>
                                <p className="text-slate-300 text-sm">{statusMessage}</p>
                            </div>
                        )}
                     </div>
                 </div>
            )}
        </div>
    );
};

const App: React.FC = () => {
  const [authState, setAuthState] = useState<AuthState>({ user: null, isAuthenticated: false });
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
        await storage.initializeDB();
        const conf = await storage.getConfig();
        setConfig(conf);
        
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            try {
                const u = JSON.parse(storedUser);
                setAuthState({ user: u, isAuthenticated: true });
            } catch (e) {}
        }
        setLoading(false);
    };
    init();
  }, []);

  const handleLogin = (user: User) => {
      setAuthState({ user, isAuthenticated: true });
      localStorage.setItem('user', JSON.stringify(user));
  };

  const handleLogout = () => {
      setAuthState({ user: null, isAuthenticated: false });
      localStorage.removeItem('user');
  };

  if (loading) {
      return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-blue-500" /></div>;
  }

  if (!authState.isAuthenticated || !authState.user) {
      return <Login onLogin={handleLogin} />;
  }

  if (authState.user.role === Role.KIOSK) {
      return <QRKiosk config={config} onLogout={handleLogout} />;
  }

  if (authState.user.role === Role.ADMIN) {
      return <AdminDashboard currentUser={authState.user} onLogout={handleLogout} />;
  }

  return <UserDashboard user={authState.user} onLogout={handleLogout} />;
};

export default App;
